import { useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import {
  ChevronLeft,
  X,
  Search as SearchIcon,
  Hash,
  Music,
  User,
  Play,
  Clock,
  Loader2,
} from 'lucide-react';
import { useSearchUsers, useSearchAudio, useSearchHashtags } from '@/hooks/useSearch';
import { processAvatarUrl } from '@/lib/mediaUtils';
import type { UserProfile, AudioSearchResult } from '@/types';

type SearchTab = 'all' | 'users' | 'songs' | 'hashtags';

interface RecentSearch {
  query: string;
  timestamp: number;
}

const RECENT_SEARCHES_KEY = 'pulse_recent_searches';
const MAX_RECENT = 10;

function getRecentSearches(): RecentSearch[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? (JSON.parse(stored) as RecentSearch[]) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(query: string): void {
  const trimmed = query.trim();
  if (!trimmed) return;
  const existing = getRecentSearches().filter((s) => s.query !== trimmed);
  const updated = [{ query: trimmed, timestamp: Date.now() }, ...existing].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
}

function clearRecentSearches(): void {
  localStorage.removeItem(RECENT_SEARCHES_KEY);
}

interface SearchProps {
  onBack?: () => void;
}

export default function Search({ onBack }: SearchProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeTab, setActiveTab] = useState<SearchTab>('all');
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>(getRecentSearches);

  const inputRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Prefix detection: # → hashtags, @ → users
  const effectiveTab = query.startsWith('#')
    ? 'hashtags'
    : query.startsWith('@')
      ? 'users'
      : activeTab;

  // Strip prefix from actual search query
  const searchQuery = debouncedQuery.replace(/^[#@]/, '');

  // Search hooks
  const {
    data: usersData,
    isLoading: usersLoading,
  } = useSearchUsers(effectiveTab === 'songs' ? '' : searchQuery);

  const {
    data: audioData,
    isLoading: audioLoading,
  } = useSearchAudio(effectiveTab === 'users' || effectiveTab === 'hashtags' ? '' : searchQuery);

  const {
    data: hashtagsData,
    isLoading: hashtagsLoading,
  } = useSearchHashtags(effectiveTab === 'users' || effectiveTab === 'songs' ? '' : searchQuery);

  const users: UserProfile[] = usersData ?? [];
  const audio: AudioSearchResult[] = audioData ?? [];
  const hashtags: { name: string }[] = hashtagsData?.items ?? [];

  const isLoading = (effectiveTab === 'all' && (usersLoading || audioLoading || hashtagsLoading))
    || (effectiveTab === 'users' && usersLoading)
    || (effectiveTab === 'songs' && audioLoading)
    || (effectiveTab === 'hashtags' && hashtagsLoading);

  const hasResults = users.length > 0 || audio.length > 0 || hashtags.length > 0;

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      if (query.trim().length >= 2) {
        saveRecentSearch(query.trim());
        setRecentSearches(getRecentSearches());
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Auto-focus input
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, []);

  // Entry animations
  useEffect(() => {
    gsap.fromTo(
      headerRef.current,
      { y: -20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out' }
    );
  }, []);

  // Stagger-in results
  useEffect(() => {
    if (resultsRef.current && resultsRef.current.children.length > 0) {
      gsap.fromTo(
        resultsRef.current.children,
        { y: 10, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.3, stagger: 0.04, ease: 'power2.out' }
      );
    }
  }, [debouncedQuery, effectiveTab, usersData, audioData, hashtagsData]);

  const handleRecentClick = useCallback((recentQuery: string) => {
    setQuery(recentQuery);
    setDebouncedQuery(recentQuery);
  }, []);

  const handleClearRecent = useCallback(() => {
    clearRecentSearches();
    setRecentSearches([]);
  }, []);

  const tabs: { key: SearchTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'users', label: 'Users' },
    { key: 'songs', label: 'Songs' },
    { key: 'hashtags', label: 'Hashtags' },
  ];

  // Determine if prefix forces a specific tab
  const isTabForced = query.startsWith('#') || query.startsWith('@');

  return (
    <div className="min-h-screen bg-[#0B0D10] pb-20">
      {/* Header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-40 bg-[#0B0D10]/95 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 pt-safe"
      >
        {/* Search bar row */}
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="w-10 h-10 rounded-full bg-[#141821] flex items-center justify-center text-[#A7B0C8] flex-shrink-0"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          <div className="flex-1 relative">
            <SearchIcon
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A7B0C8] pointer-events-none"
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users, songs, hashtags..."
              className="w-full h-10 bg-[#141821] rounded-xl pl-10 pr-10 text-sm text-[#F5F7FF] placeholder-[#A7B0C8]/50 outline-none focus:ring-1 focus:ring-[#A38BFF]/50 transition-all"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery('');
                  setDebouncedQuery('');
                  inputRef.current?.focus();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A7B0C8]"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mt-3">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              disabled={isTabForced}
              className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                (isTabForced ? effectiveTab : activeTab) === tab.key
                  ? 'bg-[#A38BFF] text-[#0B0D10]'
                  : 'bg-[#141821] text-[#A7B0C8]'
              } ${isTabForced ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Empty query → recent searches */}
        {!debouncedQuery || debouncedQuery.replace(/^[#@]/, '').length < 2 ? (
          <div>
            {recentSearches.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-[#A7B0C8]">Recent searches</h2>
                  <button
                    onClick={handleClearRecent}
                    className="text-xs text-[#A38BFF]"
                  >
                    Clear all
                  </button>
                </div>
                <div className="space-y-1">
                  {recentSearches.map((recent) => (
                    <button
                      key={recent.timestamp}
                      onClick={() => handleRecentClick(recent.query)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#141821] transition-colors text-left"
                    >
                      <Clock size={16} className="text-[#A7B0C8] flex-shrink-0" />
                      <span className="text-sm text-[#F5F7FF] truncate">{recent.query}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-16">
                <SearchIcon size={48} className="text-[#2A2F3A] mx-auto mb-4" />
                <p className="text-[#A7B0C8]">Search for users, songs, or hashtags</p>
              </div>
            )}
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={32} className="animate-spin text-[#A38BFF]" />
          </div>
        ) : !hasResults ? (
          <div className="text-center py-16">
            <SearchIcon size={48} className="text-[#2A2F3A] mx-auto mb-4" />
            <p className="text-[#A7B0C8] text-lg">No results found</p>
            <p className="text-sm text-[#A7B0C8] mt-1">
              Try a different search term
            </p>
          </div>
        ) : (
          /* Results */
          <div ref={resultsRef}>
            {effectiveTab === 'all' ? (
              <AllResults
                users={users}
                audio={audio}
                hashtags={hashtags}
                onSwitchTab={setActiveTab}
              />
            ) : effectiveTab === 'users' ? (
              <UserResults users={users} />
            ) : effectiveTab === 'songs' ? (
              <AudioResults audio={audio} />
            ) : (
              <HashtagResults hashtags={hashtags} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── All Results (stacked sections) ─── */

interface AllResultsProps {
  users: UserProfile[];
  audio: AudioSearchResult[];
  hashtags: { name: string }[];
  onSwitchTab: (tab: SearchTab) => void;
}

function AllResults({ users, audio, hashtags, onSwitchTab }: AllResultsProps) {
  return (
    <div className="space-y-6">
      {users.length > 0 && (
        <section>
          <SectionHeader title="Users" count={users.length} onSeeAll={() => onSwitchTab('users')} />
          <div className="space-y-2">
            {users.slice(0, 3).map((user) => (
              <UserCard key={user.ulid} user={user} />
            ))}
          </div>
        </section>
      )}

      {audio.length > 0 && (
        <section>
          <SectionHeader title="Songs" count={audio.length} onSeeAll={() => onSwitchTab('songs')} />
          <div className="space-y-2">
            {audio.slice(0, 3).map((item) => (
              <AudioCard key={item.ulid} item={item} />
            ))}
          </div>
        </section>
      )}

      {hashtags.length > 0 && (
        <section>
          <SectionHeader title="Hashtags" count={hashtags.length} onSeeAll={() => onSwitchTab('hashtags')} />
          <div className="space-y-2">
            {hashtags.slice(0, 3).map((tag) => (
              <HashtagCard key={tag.name} name={tag.name} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ─── Section Header ─── */

function SectionHeader({ title, count, onSeeAll }: { title: string; count: number; onSeeAll: () => void }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-semibold text-[#A7B0C8]">{title}</h3>
      {count > 3 && (
        <button onClick={onSeeAll} className="text-xs text-[#A38BFF]">
          See all
        </button>
      )}
    </div>
  );
}

/* ─── Filtered Results ─── */

function UserResults({ users }: { users: UserProfile[] }) {
  if (users.length === 0) return <EmptySection label="No users found" />;
  return (
    <div className="space-y-2">
      {users.map((user) => (
        <UserCard key={user.ulid} user={user} />
      ))}
    </div>
  );
}

function AudioResults({ audio }: { audio: AudioSearchResult[] }) {
  if (audio.length === 0) return <EmptySection label="No songs found" />;
  return (
    <div className="space-y-2">
      {audio.map((item) => (
        <AudioCard key={item.ulid} item={item} />
      ))}
    </div>
  );
}

function HashtagResults({ hashtags }: { hashtags: { name: string }[] }) {
  if (hashtags.length === 0) return <EmptySection label="No hashtags found" />;
  return (
    <div className="space-y-2">
      {hashtags.map((tag) => (
        <HashtagCard key={tag.name} name={tag.name} />
      ))}
    </div>
  );
}

function EmptySection({ label }: { label: string }) {
  return (
    <div className="text-center py-12">
      <p className="text-sm text-[#A7B0C8]">{label}</p>
    </div>
  );
}

/* ─── Result Cards ─── */

function UserCard({ user }: { user: UserProfile }) {
  const rawAvatar = user.avatarUrl || user.avatar;
  const avatarUrl = processAvatarUrl(rawAvatar);
  const displayName = user.displayName || user.name || user.username || 'Unknown';
  const username = user.username ? `@${user.username}` : '';

  return (
    <div className="flex items-center gap-3 p-3 bg-[#141821] rounded-xl">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={displayName}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-[#0B0D10] flex items-center justify-center flex-shrink-0">
          <User size={20} className="text-[#A7B0C8]" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#F5F7FF] truncate">{displayName}</p>
        {username && (
          <p className="text-xs text-[#A7B0C8] truncate">{username}</p>
        )}
        {user.bio && (
          <p className="text-xs text-[#A7B0C8] mt-0.5 line-clamp-1">{user.bio}</p>
        )}
      </div>
    </div>
  );
}

function AudioCard({ item }: { item: AudioSearchResult }) {
  const avatarUrl = processAvatarUrl(item.avatar);
  return (
    <div className="flex items-center gap-3 p-3 bg-[#141821] rounded-xl">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={item.title}
          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-[#0B0D10] flex items-center justify-center flex-shrink-0">
          <Music size={20} className="text-[#A7B0C8]" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#F5F7FF] truncate">{item.title}</p>
        <p className="text-xs text-[#A7B0C8] truncate">{item.username}</p>
      </div>
      <button className="w-10 h-10 rounded-full bg-[#A38BFF]/10 flex items-center justify-center flex-shrink-0">
        <Play size={18} className="text-[#A38BFF] ml-0.5" fill="#A38BFF" />
      </button>
    </div>
  );
}

function HashtagCard({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-[#141821] rounded-xl">
      <div className="w-12 h-12 rounded-xl bg-[#0B0D10] flex items-center justify-center flex-shrink-0">
        <Hash size={20} className="text-[#A38BFF]" />
      </div>
      <p className="text-sm font-semibold text-[#F5F7FF]">#{name}</p>
    </div>
  );
}
